import React, { useEffect, useState } from 'react'
import { TTournament } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import SectionWrapper from '../SectionWrapper/SectionWrapper'
import { useAppSelector } from '../../../redux/hooks'
import { RootState } from '../../../redux/store'
import { Link } from 'react-router-dom'
import { useTranslation } from '../../../hooks/useTranslation'

export const statusClazz = (tournament: TTournament) => {
	if (tournament) {
		if (tournament.is_upcoming) return 'gathering'
		if (tournament.is_in_progress) return 'started'
		if (tournament.is_cancelled) return 'cancelled'
		if (tournament.is_finished) return 'finished'
	}
	return ''
}

interface TournamentsSectionProps {
	id?: string
}

const TournamentsSection: React.FC<TournamentsSectionProps> = ({ id }) => {
	const tournaments = useAppSelector((state: RootState) => state.tournaments.tournaments)

	const [timeLeftForTournaments, setTimeLeftForTournaments] = useState<{ [key: number]: { days: number; hours: number; minutes: number; seconds: number } }>({})

	const { getTexts } = useTranslation(true)

	useEffect(() => {
		const calculateTimeLeft = (targetTime: number) => {
			const now = new Date().getTime()
			const timeDiff = targetTime - now

			if (timeDiff > 0) {
				const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
				const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
				const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
				const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
				return { days, hours, minutes, seconds }
			} else {
				return { days: 0, hours: 0, minutes: 0, seconds: 0 }
			}
		}

		const updateTournamentTimes = () => {
			const updatedTimeLeft: { [key: number]: { days: number; hours: number; minutes: number; seconds: number } } = {}
			tournaments?.forEach((tournament) => {
				const targetTime = tournament.is_in_progress ? new Date(tournament.end_time).getTime() : new Date(tournament.start_time).getTime()
				updatedTimeLeft[tournament.instance_id] = calculateTimeLeft(targetTime)
			})
			setTimeLeftForTournaments(updatedTimeLeft)
		}

		const interval = setInterval(updateTournamentTimes, 1000)

		return () => clearInterval(interval)
	}, [tournaments])

	const getBuyInText = (tournament: TTournament) => {
		if (tournament.registration_cost_points) {
			return `${tournament.registration_cost_points} points`;
		}
		if (tournament.registration_cost_gems) {
			return `${tournament.registration_cost_gems} gems`;
		}
		if (tournament.registration_cost_diamonds) {
			return `${tournament.registration_cost_diamonds} diamonds`;
		}

		return 'Free';
	};

	return (
		<SectionWrapper title={getTexts("tournaments_section")} id={id} >
			{tournaments.map((tournament, index) => {
				const timeLeft = timeLeftForTournaments[tournament.instance_id] || { days: 0, hours: 0, minutes: 0, seconds: 0 }
				const statusKey = statusClazz(tournament);
				return (
					<Link to={`/tournament/${tournament.instance_id}`} className="tournament-card" key={tournament.instance_id}>
						<div className="tournament-content" style={{ backgroundImage: `url('${tournament.image1}')` }}>
							<div className={`tournament-status ${statusKey}`}>{statusKey && getTexts(statusKey)}</div>
							<div className="overlay"></div>
							{tournament.ribbon && <div className={`ribbon ${tournament.ribbon}`}>{tournament.ribbon}</div>}
							<div className="tournament-title" dangerouslySetInnerHTML={{ __html: tournament.name }}></div>
						</div>
						<div className="tournament-info">
							<div className="info-section">
								<div className="info-title">{getTexts('prize')}</div>
								<div className="info-value">{tournament.prize_pool_short ? tournament.prize_pool_short : 'No prize'}</div>
							</div>
							<div className="info-separator"></div>
							<div className="info-section">
								<div className="info-title">{getTexts('buy_in')}</div>
								<div className="info-value">{getBuyInText(tournament)}</div>
							</div>
							<div className="info-separator"></div>
							<div className="info-section">
								<div className="info-title">{getTexts('registered')}</div>
								<div className="info-value">
									{tournament.registration_count}/{tournament.players_max_count}
								</div>
							</div>
						</div>
						<div className="tournament-bottom">
							<div className="tournament-timer">
								<div className="timer-title">
									{tournament.is_upcoming && 'Starts in'}
									{tournament.is_in_progress && 'Finishing in'}
									{tournament.is_finished && 'Finished'}
									{tournament.is_cancelled && 'Cancelled'}
								</div>
								<div className="timer-value">
									<span className="time-section">
										{String(timeLeft.days).padStart(2, '0')}
										<span className="time-unit">{getTexts('days_short')}</span>
									</span>
									<span className="time-section">
										{String(timeLeft.hours).padStart(2, '0')}
										<span className="time-unit">{getTexts('hours_short')}</span>
									</span>
									<span className="time-section">
										{String(timeLeft.minutes).padStart(2, '0')}
										<span className="time-unit">{getTexts('minutes_short')}</span>
									</span>
									<span className="time-section">
										{String(timeLeft.seconds).padStart(2, '0')}
										<span className="time-unit">{getTexts('seconds_short')}</span>
									</span>
								</div>
							</div>

							{!tournament.is_user_registered && tournament.is_can_register && <div className={`btn`}>{getTexts('join_free')}</div>}
						</div>
					</Link>
				)
			})}
		</SectionWrapper>
	)
}

export default TournamentsSection
